<template>
    <div class="vi-alert" :class="[`vi-alert--${variant}`, {'vi-alert--solid': solid}]" role="alert">
        <i v-if="resolvedIcon" class="vi-alert__icon ki-filled" :class="resolvedIcon"></i>
        <div class="vi-alert__body">
            <div v-if="title" class="vi-alert__title">{{ title }}</div>
            <div class="vi-alert__content"><slot>{{ message }}</slot></div>
        </div>
    </div>
</template>

<script>
const VARIANT_ICONS = {
    info: 'ki-information-2',
    success: 'ki-check-circle',
    warning: 'ki-information-4',
    danger: 'ki-cross-circle',
    neutral: 'ki-information-2',
    primary: 'ki-information-2',
}

export default {
    name: 'Alert',
    props: {
        // info | success | warning | danger | neutral | primary
        variant: {type: String, default: 'info'},
        title: {type: String, default: ''},
        message: {type: String, default: ''},
        // custom keenicon class (e.g. 'ki-lock-2'); pass false to hide the icon
        icon: {type: [String, Boolean], default: undefined},
        // solid border instead of the default dashed border
        solid: {type: Boolean, default: false},
    },
    computed: {
        resolvedIcon() {
            if (this.icon === false) return null
            if (typeof this.icon === 'string' && this.icon) return this.icon
            return VARIANT_ICONS[this.variant] || VARIANT_ICONS.info
        },
    },
}
</script>

<style scoped>
.vi-alert {
    display: flex;
    align-items: flex-start;
    gap: .5rem;
    border-radius: .5rem;
    padding: .5rem .75rem;
    border: 1px dashed color-mix(in srgb, var(--vi-accent) 30%, transparent);
    background-color: color-mix(in srgb, var(--vi-accent) 8%, transparent);
}

.vi-alert--solid {
    border-style: solid;
}

.vi-alert__icon {
    font-size: .875rem;
    line-height: 1;
    margin-top: 1px;
    flex-shrink: 0;
    color: var(--vi-accent);
}

.vi-alert__body {
    min-width: 0;
}

.vi-alert__title {
    font-weight: 600;
    font-size: 11px;
    line-height: 1.4;
    margin-bottom: 2px;
    color: color-mix(in srgb, var(--vi-accent) 82%, var(--foreground));
}

.vi-alert__content {
    font-size: 11px;
    line-height: 1.5;
    color: color-mix(in srgb, var(--vi-accent) 68%, var(--foreground));
}

/* variant accent colors (theme palette tokens) */
.vi-alert--info {
    --vi-accent: var(--color-blue-500);
}

.vi-alert--success {
    --vi-accent: var(--color-green-600);
}

.vi-alert--warning {
    --vi-accent: var(--color-yellow-600);
}

.vi-alert--danger {
    --vi-accent: var(--color-red-600);
}

.vi-alert--neutral {
    --vi-accent: var(--color-zinc-500);
}

.vi-alert--primary {
    --vi-accent: var(--primary);
}

/* dark mode: strengthen the tint/border so it stays visible on dark surfaces */
:global(.dark) .vi-alert {
    background-color: color-mix(in srgb, var(--vi-accent) 16%, transparent);
    border-color: color-mix(in srgb, var(--vi-accent) 42%, transparent);
}
</style>
